import { Fragment, ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useForm } from 'react-final-form'
import styled from 'styled-components'

import Block from 'src/components/layout/Block'
import { extraSmallFontSize, fontColor, lg, md, secondary, sm, xs } from 'src/theme/variables'
import Paragraph from 'src/components/layout/Paragraph'
import Field from 'src/components/forms/Field'
import TextField from 'src/components/forms/TextField'
import { providerNameSelector, shouldSwitchWalletChain } from 'src/logic/wallets/store/selectors'
import {
  FIELD_CREATE_CUSTOM_SAFE_NAME,
  FIELD_CREATE_SUGGESTED_SAFE_NAME,
  FIELD_MAX_OWNER_NUMBER,
  FIELD_NEW_SAFE_THRESHOLD,
  FIELD_CREATE_SAFE_OWNERS_LIST,
  FIELD_NEW_SAFE_OWNER_ENS_LIST,
} from '../fields/starterFields'
import { useStepper } from 'src/components/StepperWalliro/stepperContext'
import NetworkLabel from 'src/components/NetworkLabel/NetworkLabel'
import { reverseENSLookup } from 'src/logic/wallets/getWeb3'
import { trackEvent } from 'src/utils/googleTagManager'
import { CREATE_SAFE_EVENTS } from 'src/utils/events/createLoadSafe'
import Hairline from 'src/components/layout/Hairline'
import Row from 'src/components/layout/Row'
import Col from 'src/components/layout/Col'
import { CheckCircle } from '@material-ui/icons'
import Button from 'src/components/layout/Button'
import ButtonHelper from 'src/components/ButtonHelper'
import { Icon, Text } from '@gnosis.pm/safe-react-components'
import { sameString } from 'src/utils/strings'
import {
  ADDRESS_REPEATED_ERROR,
  composeValidators,
  minMaxLength,
  minValue,
  required,
  THRESHOLD_ERROR,
} from 'src/components/forms/validator'
import AddressInput from 'src/components/forms/AddressInput'
import { currentNetworkAddressBookAsMap } from 'src/logic/addressBook/store/selectors'
import InputAdornment from '@material-ui/core/InputAdornment'
import { ScanQRWrapper } from 'src/components/ScanQRModal/ScanQRWrapper'
import SelectField from 'src/components/forms/SelectField'
import MenuItem from '@material-ui/core/MenuItem'
import { objectByString } from 'src/utils/objects'

export const nameNewSafeStepLabel = 'Name'

const CreateWalliro = () => {
  // const [ownersWithENSName, setOwnersWithENSName] = useState<Record<string, string>>({})
  const providerName = useSelector(providerNameSelector)
  const isWrongNetwork = useSelector(shouldSwitchWalletChain)

  const createNewSafeForm = useForm()
  const formValues = createNewSafeForm.getState().values
  const hasCustomSafeName = !!objectByString(formValues, FIELD_CREATE_CUSTOM_SAFE_NAME)

  useEffect(() => {
    // On unmount, e.g. go back/next
    return () => {
      if (hasCustomSafeName) {
        trackEvent(CREATE_SAFE_EVENTS.NAME_SAFE)
      }
    }
  }, [hasCustomSafeName])

  useEffect(() => {
    const getInitialOwnerENSNames = async () => {
      const formValues = createNewSafeForm.getState().values
      const owners = objectByString(formValues, FIELD_CREATE_SAFE_OWNERS_LIST)
      const ownersWithENSName = await Promise.all(
        owners
          .filter(({ addressFieldName }) => !!objectByString(formValues, addressFieldName))
          .map(async ({ addressFieldName }) => {
            const address = objectByString(formValues, addressFieldName)
            const ensName = await reverseENSLookup(address)
            return {
              address,
              name: ensName,
            }
          }),
      )

      const ownersWithENSNameRecord = ownersWithENSName.reduce<Record<string, string>>((acc, { address, name }) => {
        return {
          ...acc,
          [address]: name,
        }
      }, {})

      createNewSafeForm.change(FIELD_NEW_SAFE_OWNER_ENS_LIST, ownersWithENSNameRecord)
      // setOwnersWithENSName(ownersWithENSNameRecord)
    }
    getInitialOwnerENSNames()
  }, [createNewSafeForm])

  // useEffect(() => {
  //   if (ownersWithENSName) {
  //     createNewSafeForm.change(FIELD_NEW_SAFE_OWNER_ENS_LIST, ownersWithENSName)
  //   }
  // }, [ownersWithENSName, createNewSafeForm])

  const addressBook = useSelector(currentNetworkAddressBookAsMap)
  const formErrors = createNewSafeForm.getState().errors || {}

  const owners = objectByString(formValues, FIELD_CREATE_SAFE_OWNERS_LIST)
  const ownersWithENSName = objectByString(formValues, FIELD_NEW_SAFE_OWNER_ENS_LIST)
  const threshold = objectByString(formValues, FIELD_NEW_SAFE_THRESHOLD)
  const maxOwnerNumber = objectByString(formValues, FIELD_MAX_OWNER_NUMBER)

  function onClickAddNewOwner() {
    const newEmptyOwner = {
      nameFieldName: `owner-name-${maxOwnerNumber}`,
      addressFieldName: `owner-address-${maxOwnerNumber}`,
    }

    createNewSafeForm.change(FIELD_CREATE_SAFE_OWNERS_LIST, [...owners, newEmptyOwner])
    // createNewSafeForm.change(FIELD_CREATE_SAFE_OWNERS_LIST, [...owners, newEmptyOwner])
    const updatedMaxOwnerNumbers = maxOwnerNumber + 1
    createNewSafeForm.change(FIELD_MAX_OWNER_NUMBER, updatedMaxOwnerNumbers)
  }

  function onClickRemoveOwner({ addressFieldName }) {
    const ownersUpdated = owners.filter((owner) => owner.addressFieldName !== addressFieldName)
    createNewSafeForm.change(FIELD_CREATE_SAFE_OWNERS_LIST, ownersUpdated)
    createNewSafeForm.change('create.' + addressFieldName, undefined)

    const updatedMaxOwnerNumbers = maxOwnerNumber - 1
    createNewSafeForm.change(FIELD_MAX_OWNER_NUMBER, updatedMaxOwnerNumbers)

    const hasToUpdateThreshold = threshold > ownersUpdated.length
    if (hasToUpdateThreshold) {
      createNewSafeForm.change(FIELD_NEW_SAFE_THRESHOLD, threshold - 1)
    }
  }

  const getENSName = async (address: string): Promise<void> => {
    const ensName = await reverseENSLookup(address)
    createNewSafeForm.change(FIELD_NEW_SAFE_OWNER_ENS_LIST, { ...ownersWithENSName, [address]: ensName })
  }

  const handleScan = async (address: string, closeQrModal: () => void, addressFieldName: string): Promise<void> => {
    const scannedAddress = address.startsWith('ethereum:') ? address.replace('ethereum:', '') : address
    await getENSName(scannedAddress)
    createNewSafeForm.change(addressFieldName, scannedAddress)
    closeQrModal()
  }

  return (
    <Fragment>
      <BlockWithPadding data-testid={'create-safe-name-step'}>
        <Block margin="md">
          <Paragraph color="primary" noMargin size="lg">
            You are about to make a new Walliro wallet with one or multiple owners. Firstly, give your new wallet a
            name. This name is only stored locally and will never be shared with Walliro or any third parties. The new
            Walliro will ONLY be available on <NetworkLabel />
          </Paragraph>
        </Block>
        <label htmlFor={FIELD_CREATE_CUSTOM_SAFE_NAME}>Name of the new Walliro</label>
        <FieldContainer margin="lg">
          <Col xs={11}>
            <Field
              component={TextField}
              name={FIELD_CREATE_CUSTOM_SAFE_NAME}
              placeholder={objectByString(formValues, FIELD_CREATE_SUGGESTED_SAFE_NAME)}
              label="Walliro name"
              type="text"
              testId="create-safe-name-field"
            />
          </Col>
        </FieldContainer>
        <Block margin="lg">
          <Paragraph color="primary" noMargin size="lg">
            By continuing you agree to the{' '}
            <Link href="https://walliro.com/privacy" rel="noopener noreferrer" target="_blank">
              terms of use and privacy policy.
            </Link>
            .
          </Paragraph>
        </Block>
      </BlockWithPadding>
      <Hairline />
      <BlockWithPadding data-testid={'create-safe-owners-confirmation-step'}>
        <ParagraphWithMargin color="primary" noMargin size="lg">
          Your Walliro will have one or more owners. We have prefilled the first owner with your connected wallet
          details, but you can change this to another owner.
        </ParagraphWithMargin>
        <Paragraph color="primary" size="lg">
          Add additional owners (e.g. wallets of your teammates) and specify how many of them have to confirm a
          transaction before it gets executed. In general, the more confirmations required, the more secure your Walliro
          is.
          <StyledLink
            // href="https://help.gnosis-safe.io/en/articles/4772567-what-gnosis-safe- setup-should-i-use"
            href="https://support.walliro.com/what-walliro-setup-should-i-use/"
            target="_blank"
            rel="noreferrer"
            title="Learn about which Walliro setup to use"
          >
            <Text size="xl" as="span" color="primary">
              Learn about which Walliro setup to use
            </Text>
            <Icon size="sm" type="externalLink" color="primary" />
          </StyledLink>
          . The new Walliro will ONLY be available on <NetworkLabel />
        </Paragraph>
      </BlockWithPadding>
      <Hairline />
      <RowHeader>
        <Col xs={3}>NAME</Col>
        <Col xs={7}>ADDRESS</Col>
      </RowHeader>
      <Hairline />
      <Block margin="md" padding="md">
        <RowHeader>
          {owners.map(({ nameFieldName, addressFieldName }, i: number) => {
            const hasOwnerAddressError = objectByString(formErrors, 'create.' + addressFieldName)
            const ownerAddress = objectByString(formValues, addressFieldName)
            const showDeleteIcon = addressFieldName !== 'owner-address-0' // we hide de delete icon for the first owner
            const ownerName = ownersWithENSName[ownerAddress] || 'Owner Name'
            const isRepeated = (value: string) => {
              const formValues = createNewSafeForm.getState().values
              const prevOwners = owners.filter((_: typeof owners[number], index: number) => index !== i)
              const repeated = prevOwners.some((owner: typeof owners[number]) => {
                return sameString(objectByString(formValues, 'create.' + owner.addressFieldName), value)
              })

              return repeated ? ADDRESS_REPEATED_ERROR : undefined
            }

            return (
              <Fragment key={addressFieldName}>
                <Col xs={3}>
                  <OwnerNameField
                    component={TextField}
                    name={'create.' + nameFieldName}
                    placeholder={ownerName}
                    label="Owner Name"
                    type="text"
                    validate={minMaxLength(0, 50)}
                    testId={nameFieldName}
                  />
                </Col>
                <Col xs={7}>
                  <AddressInput
                    fieldMutator={async (address) => {
                      createNewSafeForm.change(addressFieldName, address)
                      const addressName = addressBook[address]?.name
                      if (!addressName) {
                        await getENSName(address)
                      }
                    }}
                    inputAdornment={
                      !hasOwnerAddressError && {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CheckIconAddressAdornment data-testid={`${addressFieldName}-valid-adornment`} />
                          </InputAdornment>
                        ),
                      }
                    }
                    name={'create.' + addressFieldName}
                    placeholder="Owner Address*"
                    text="Owner Address"
                    testId={addressFieldName}
                    validators={[isRepeated]}
                  />
                </Col>
                <OwnersIconsContainer xs={1} center="xs" middle="xs">
                  <ScanQRWrapper
                    handleScan={(...args) => handleScan(...args, addressFieldName)}
                    testId={`${addressFieldName}-scan-QR`}
                  />
                </OwnersIconsContainer>
                {showDeleteIcon && (
                  <OwnersIconsContainer xs={1} center="xs" middle="xs">
                    <ButtonHelper
                      onClick={() => onClickRemoveOwner({ addressFieldName })}
                      dataTestId={`${addressFieldName}-remove-button`}
                    >
                      <Icon size="sm" type="delete" color="icon" tooltip="Delete Owner" />
                    </ButtonHelper>
                  </OwnersIconsContainer>
                )}
              </Fragment>
            )
          })}
        </RowHeader>
        <OwnerContainer align="center" grow>
          <Button color="secondary" data-testid="add-new-owner" onClick={onClickAddNewOwner}>
            <Paragraph noMargin size="lg">
              + Add another owner
            </Paragraph>
          </Button>
        </OwnerContainer>
        <BlockWithPadding>
          <Block>
            <Paragraph>Any transaction requires the confirmation of:</Paragraph>
          </Block>
          <OwnerContainer align="center" grow>
            <Col xs={1}>
              <Field
                component={SelectField}
                data-testid="threshold-selector-input"
                name={FIELD_NEW_SAFE_THRESHOLD}
                validate={(val) => {
                  const isValidThreshold = () => {
                    return !!val && Number(val) <= owners.length ? undefined : THRESHOLD_ERROR
                  }
                  return composeValidators(required, minValue(1), isValidThreshold)(val)
                }}
              >
                {owners.map((_, option) => (
                  <MenuItem
                    key={`threshold-selector-option-${option}`}
                    value={option + 1}
                    data-testid={`threshold-selector-option-${option + 1}`}
                  >
                    {option + 1}
                  </MenuItem>
                ))}
              </Field>
            </Col>
            <Col xs={11}>
              <StyledParagraph noMargin>out of {owners.length} owner(s)</StyledParagraph>
            </Col>
          </OwnerContainer>
        </BlockWithPadding>
      </Block>
    </Fragment>
  )
}

export default CreateWalliro

const BlockWithPadding = styled(Block)`
  padding: ${lg};
`

const FieldContainer = styled(Block)`
  display: flex;
  max-width: 480px;
  margin-top: 12px;
`

const Link = styled.a`
  color: ${secondary};
`

const ParagraphWithMargin = styled(Paragraph)`
  margin-bottom: 12px;
`

const StyledLink = styled(Link)`
  padding: 0 ${xs};
  & svg {
    position: relative;
    top: 1px;
    left: ${xs};
    height: 14px;
    width: 14px;
  }
`
const RowHeader = styled(Row)`
  padding: ${sm} ${lg};
  font-size: ${extraSmallFontSize};
  color: ${fontColor};
`

const OwnerNameField = styled(Field)`
  margin-right: ${md};
  margin-bottom: ${md};
`

const CheckIconAddressAdornment = styled(CheckCircle)`
  color: #03ae60;
  height: 20px;
`

const OwnersIconsContainer = styled(Col)`
  height: 56px;
  max-width: 32px;
  cursor: pointer;
`

const OwnerContainer = styled(Row)`
  justify-content: center;
`

const StyledParagraph = styled(Paragraph)`
  padding-left: 12px;
`
